import React, { useState, useEffect } from "react";
import { Switch } from "antd";
import toast from "react-hot-toast";
import Pagination from "../../components/global/pagination/Pagination.jsx";
import columns from "../../columns/MT5Account/MT5AcceptedAccounts.js";
import TableWithSkeleton from "../../components/global/table/TableWithSkeleton.jsx";
import {
  GetAllAccountApi,
  GetAllMT5ByIdApi,
  SetMT5AutoTransferApprovalApi,
} from "../../api/request/account.js";
import ConfirmationModal from "../../components/global/modal/ConfirmationModal.jsx";
import ModalComponent from "../../components/global/modal/ModalComponent.jsx";
import moment from "moment";
import Eye from "../../assets/Eye.jsx";
import UserAvatar from "../../components/global/avtar/UserAvatar.jsx";
import useModulePermissions from "../../hooks/useModulePermissions.jsx";

export default function Accounts({
  dates,
  selectedRowKeys,
  setSelectedRowKeys,
  isRefresh,
}) {
  const accountPermissions = useModulePermissions('account');
  const canUpdate = !!accountPermissions.update;

  const [isLoading, setIsLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [data, setData] = useState([]);
  const [selectedMT5Id, setSelectedMT5Id] = useState("");
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [mt5Details, setMT5Details] = useState({});
  const [sorter, setSorter] = useState({ SortBy: "actionDate", SortOrder: "" });
  const [isAutoApproveConfirmOpen, setIsAutoApproveConfirmOpen] = useState(false);
  const [isAutoApproveLoading, setIsAutoApproveLoading] = useState(false);
  const [selectedAutoApproveAccount, setSelectedAutoApproveAccount] = useState(null);

  const handleAutoApproveClick = (login, checked) => {
    setSelectedAutoApproveAccount({ login, checked });
    setIsAutoApproveConfirmOpen(true);
  };

  const handleAutoApproveConfirm = async () => {
    try {
      setIsAutoApproveLoading(true);
      const { data } = await SetMT5AutoTransferApprovalApi({
        accountNumber: [selectedAutoApproveAccount?.login],
        isAuto: selectedAutoApproveAccount?.checked,
      });
      if (data?.success) {
        toast.success(data?.message || "Auto approve updated successfully");
        getAllMt5();
      } else {
        toast.error(data?.message);
      }
      setIsAutoApproveLoading(false);
      setIsAutoApproveConfirmOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Failed to update auto approve");
      setIsAutoApproveLoading(false);
      setIsAutoApproveConfirmOpen(false);
    }
  };

  const getAllMt5 = async () => {
    try {
      let params = {
        PageNumber: page,
        PageSize: pageSize,
        SearchText: dates,
        FilterText: "Approved",
        ...sorter,
      };
      setIsLoading(true);
      const { data } = await GetAllAccountApi(
        new URLSearchParams(params).toString()
      );
      if (data?.success) {
        setData(
          data?.data?.map((item, index) => {
            return {
              ...item,
              key: item?.login || index,
              userDetails: (
                <UserAvatar
                  data={{
                    name: item?.name,
                    email: item?.email,
                    profileImage: item?.profileImage,
                  }}
                />
              ),
              balance: `$ ${item?.balance}`,
              actionDate: item?.actionDate
                ? moment(item?.actionDate).format("DD-MM-YYYY, hh:mm A")
                : null,

              autoApprove: (
                <Switch
                  disabled={!canUpdate}
                  onChange={(checked) => handleAutoApproveClick(item?.login, checked)}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                  checked={item?.isAutoIT}
                />
              ),
              actions: (
                <div className="flex">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedMT5Id(item?.login);
                      setIsOpenModal(true);
                    }}
                  >
                    <Eye />
                  </button>
                </div>
              ),
            };
          })
        );
        setTotalRecord(data?.totalRecords);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter?.order) {
      setSorter({ SortBy: sorter?.field, SortOrder: sorter?.order });
    } else {
      setSorter({ SortBy: "actionDate", SortOrder: "" });
    }
  };

  useEffect(() => {
    getAllMt5();
  }, [page, pageSize, filter, sorter, dates, isRefresh]);

  useEffect(() => {
    setPage(1);
  }, [dates]);

  const getMT5ById = async () => {
    try {
      const { data } = await GetAllMT5ByIdApi(selectedMT5Id);
      setMT5Details(data?.data);
    } catch (error) {}
  };

  useEffect(() => {
    if (selectedMT5Id) getMT5ById();
  }, [selectedMT5Id]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => setSelectedRowKeys(selectedRowKeys),
  };

  return (
    <div>
      <div className="my-6">
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <TableWithSkeleton
            columns={columns}
            data={data}
            loading={isLoading}
            onChange={handleTableChange}
            hiddenColumns={!canUpdate ? ["Actions"] : []}
            page={page}
            pageSize={pageSize}
            rowSelection={rowSelection}
          />
        </div>
        <Pagination
          total={totalRecord}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </div>

      {/* ---- MT5 Account Information ---- */}
      <ModalComponent
        isOpen={isOpenModal}
        setIsOpen={setIsOpenModal}
        title="MT5 Account Information"
        width={1300}
      >
        <div className="p-5">
          <div className="grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-10 ">
            <div>
              <p className="text-description font-normal ">First Name</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.firstName || "--"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Last Name</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.lastName || "--"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Account No.</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.login || "--"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Country</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.country || "--"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Credit</p>
              <p className="text-description primary font-semibold  mt-2">
                {" "}
                {mt5Details?.credit || "0"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Balance</p>
              <p className="text-description primary font-semibold  mt-2">
                $ {mt5Details?.balance || "0"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Leverage</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.leverage || "0"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Margin</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.margin || "0"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Margin Free</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.marginFree || "0"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Group</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.group || "--"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Profit</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.profit || "0"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Commission</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.Commission || "0"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Equity</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.equity || "0"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Balance Prev Day</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.balancePrecDay || "0"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Equity Prev Day</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.equityPrecDay || "0"}
              </p>
            </div>
            {mt5Details?.status && (
              <div>
                <p className="text-description font-normal ">Status</p>
                <p className="text-description primary font-semibold  mt-2">
                  {mt5Details?.status || "--"}
                </p>
              </div>
            )}
            <div>
              <p className="text-description font-normal ">Trading Password</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.tradingPassword || "---"}
              </p>
            </div>
            <div>
              <p className="text-description font-normal ">Investor Password</p>
              <p className="text-description primary font-semibold  mt-2">
                {mt5Details?.investorPassword || "---"}
              </p>
            </div>
          </div>
        </div>
      </ModalComponent>

      <ConfirmationModal
        isOpen={isAutoApproveConfirmOpen}
        setIsOpen={setIsAutoApproveConfirmOpen}
        type="success"
        message="Are you sure you want to change the auto approve status?"
        onConfirm={handleAutoApproveConfirm}
        isLoading={isAutoApproveLoading}
        loadingLabel="Updating..."
      />
    </div>
  );
}
